/**
 * API client for project tasks.
 *
 * Tasks are scoped to a project, can be linked to frames, and carry ticket
 * numbers for display.
 */

import { apiRequest } from "./client";

/* ---------- Types ---------- */

export type TaskSource =
  | "note"
  | "chat"
  | "comment_mention"
  | "task_assigned"
  | "delegation"
  | "event"
  | "heartbeat"
  | "schedule"
  | "template";

export type TaskStatus =
  | "backlog"
  | "todo"
  | "in_progress"
  | "in_review"
  | "done"
  | "blocked";

export interface TaskAgent {
  id: string;
  name: string;
  color: string;
}

export interface TaskUser {
  id: string;
  email: string;
  name: string;
}

export interface TaskDependency {
  id: string;
  title: string;
  ticket_display: string;
  status: string;
}

export interface TaskLastComment {
  author: string;
  content: string;
  created_at: string;
}

export interface TaskChildSummary {
  id: string;
  ticket_display: string | null;
  title: string;
  status: TaskStatus;
  agent_name: string | null;
}

export interface TaskChildTasksSummary {
  count: number;
  counts_by_status: Partial<Record<TaskStatus, number>>;
  children: TaskChildSummary[];
}

export interface TaskParentSummary {
  id: string;
  ticket_display: string | null;
  title: string;
}

export type ScheduleType = "once" | "recurring";
export type Recurrence =
  | "daily"
  | "weekdays"
  | "weekly"
  | "biweekly"
  | "monthly";

export interface TaskSchedule {
  id: string;
  schedule_type: ScheduleType;
  scheduled_at: string | null;
  rrule: Recurrence | "";
  rrule_time: string | null;
  rrule_day: number | null;
  timezone: string;
  next_run_at: string | null;
  last_run_at: string | null;
  run_count: number;
  is_active: boolean;
  max_runs: number | null;
}

export interface TaskScheduleInput {
  schedule_type: ScheduleType;
  scheduled_at?: string | null;
  rrule?: Recurrence | "";
  rrule_time?: string | null;
  rrule_day?: number | null;
  timezone?: string;
  max_runs?: number | null;
  is_active?: boolean;
}

/** Persisted app destinations on a task. */
export interface TaskAppTarget {
  appId: string;
  route: "ax" | "mcp" | "provider";
  confirmed: boolean;
  url?: string;
  label?: string;
  capturedAt?: string;
}

export interface Task {
  id: string;
  /** Exact turn that delegated this native/direct task; null for ordinary tasks. */
  origin_execution_id?: string | null;
  project: string;
  title: string;
  description?: string;
  app_targets?: TaskAppTarget[];
  source: TaskSource;
  status: TaskStatus;
  priority: string;
  frame: string | null;
  /** Inline comment anchor — set when the task was created from a text
   * selection in `frame`. The exact substring is used to re-render the
   * blue comment-highlight + badge on the frame. */
  text_anchor: { text: string } | null;
  assigned_agent: string | null;
  assigned_agent_detail: TaskAgent | null;
  assigned_user: string | null;
  assigned_user_detail: TaskUser | null;
  native_subagent?: boolean;
  /** Actual worker launch receipt; null while a saved choice has not started. */
  worker_started_at?: string | null;
  blocked_by_detail: TaskDependency[];
  blocks_detail: TaskDependency[];
  comment_count: number;
  last_comment: TaskLastComment | null;
  is_checked_out: boolean;
  checkout_agent_key: string;
  ticket_number: number | null;
  ticket_display: string | null;
  parent_task: string | null;
  /**
   * Delegation shape — set on the PARENT task at delegation time.
   * `cross_agent` fan-out to different agents; `self_parallel` fan-out
   * to the same agent. Empty string on tasks that never delegated, or on
   * legacy tasks from before Sprint 2.
   */
  delegation_kind?: "cross_agent" | "self_parallel" | "";
  /** Delegation rollup — present when this task has at least one child. */
  child_tasks_summary?: TaskChildTasksSummary | null;
  /** Parent summary — present when `parent_task` is set (delegation child). */
  parent_task_summary?: TaskParentSummary | null;
  due_at: string | null;
  schedule: TaskSchedule | null;
  /** Heartbeat budget spent: the agent stops waking itself on this task
   * until a human replies on it or moves it. */
  needs_attention: boolean;
  /** Heartbeat-driven runs since the last human touch. */
  autonomous_attempts: number;
  /** Ship-code: branch name checked out for this task (worktree). */
  branch_name?: string | null;
  /** Ship-code: upstream branch the worktree was created from. */
  base_branch?: string | null;
  /** Ship-code: branch this task's PR targets (defaults to base_branch). */
  pr_target_branch?: string | null;
  /** Ship-code lifecycle phase. Empty string when not a coding task. */
  ship_phase?: string;
  /** URL of the open PR for this task, if one has been pushed. */
  pr_url?: string;
  /** PR number assigned by GitHub, if a PR exists. */
  pr_number?: number | null;
  /** Rollup of CI checks on the PR (companion gh-status). */
  checks_status?: "none" | "success" | "failure" | "pending";
  /** Frame IDs of deliverables produced by this task. */
  deliverable_frame_ids?: string[];
  /** Direct coding session bound to this task, if Denker dispatched one. */
  direct_session?: {
    provider: string;
    status: string;
    running_in: string | null;
    open_in_app_url: string | null;
    attach_command: string | null;
    has_pending_approval: boolean;
    pending_approval_id: string | null;
    /** Every pending approval, oldest first. */
    pending_approval_ids: string[];
    has_queued_message: boolean;
  } | null;
  created_at: string;
  updated_at: string;
}

interface ApiResponse<T> {
  status: "success";
  data: T;
}

type LegacyTaskWire = Task & {
  /** @deprecated Accepted only from older servers and removed at the boundary. */
  space?: string;
};

function normalizeTask(task: LegacyTaskWire): Task {
  const { space: _legacySpace, ...canonicalTask } = task;
  return canonicalTask;
}

function canonicalTaskBody<T extends object>(data: T): Omit<T, "space"> {
  const { space: _legacySpace, ...body } = data as T & { space?: unknown };
  return body;
}

/* ---------- Tasks ---------- */

export async function createTask(
  projectId: string,
  data: {
    title: string;
    source: TaskSource;
    status?: TaskStatus;
    priority?: string;
    description?: string;
    due_at?: string | null;
    frame?: string | null;
    text_anchor?: { text: string } | null;
    assigned_agent?: string | null;
    assigned_user?: string | null;
    blocked_by?: string[];
    parent_task?: string | null;
    image_ids?: string[];
    schedule?: TaskScheduleInput | null;
  },
): Promise<Task> {
  const res = await apiRequest<ApiResponse<LegacyTaskWire>>(
    `/api/v1/projects/${projectId}/tasks/`,
    { method: "POST", body: canonicalTaskBody(data) },
  );
  return normalizeTask(res.data);
}

/**
 * The board's tasks across the user's active projects: the newest-updated
 * open tasks and the newest done ones per project. `truncated` says older
 * open tasks were left out.
 */
export async function listTasks(): Promise<{
  tasks: Task[];
  truncated: boolean;
}> {
  const res = await apiRequest<
    ApiResponse<LegacyTaskWire[]> & { truncated?: boolean }
  >("/api/v1/tasks/");
  return { tasks: res.data.map(normalizeTask), truncated: !!res.truncated };
}

/** One task by id, also one the board's list left out (an older done one). */
export async function getTask(
  projectId: string,
  taskId: string,
): Promise<Task> {
  const res = await apiRequest<ApiResponse<LegacyTaskWire>>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/`,
    { silent: true },
  );
  return normalizeTask(res.data);
}

export async function updateTask(
  projectId: string,
  taskId: string,
  data: {
    title?: string;
    description?: string;
    priority?: string;
    status?: TaskStatus;
    comment?: string;
    comment_frame_id?: string | null;
    agent_id?: string | null;
    assigned_agent?: string | null;
    assigned_user?: string | null;
    schedule?: TaskScheduleInput | null;
  },
): Promise<Task> {
  const res = await apiRequest<ApiResponse<LegacyTaskWire>>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/`,
    { method: "PATCH", body: canonicalTaskBody(data) },
  );
  return normalizeTask(res.data);
}

export async function addTaskComment(
  projectId: string,
  taskId: string,
  comment: string,
  data?: {
    comment_frame_id?: string | null;
    agent_id?: string | null;
  },
): Promise<Task> {
  return updateTask(projectId, taskId, { comment, ...data });
}

export async function deleteTask(
  projectId: string,
  taskId: string,
): Promise<void> {
  await apiRequest(`/api/v1/projects/${projectId}/tasks/${taskId}/`, {
    method: "DELETE",
  });
}

export async function updateTaskStatus(
  projectId: string,
  taskId: string,
  status: TaskStatus,
): Promise<Task> {
  const res = await apiRequest<ApiResponse<LegacyTaskWire>>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/status/`,
    { method: "POST", body: { status } },
  );
  return normalizeTask(res.data);
}

/* ---------- Task Schedule ---------- */

export async function pauseSchedule(
  projectId: string,
  taskId: string,
): Promise<TaskSchedule> {
  const res = await apiRequest<ApiResponse<TaskSchedule>>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/schedule/`,
    { method: "POST" },
  );
  return res.data;
}

export async function resumeSchedule(
  projectId: string,
  taskId: string,
): Promise<TaskSchedule> {
  const res = await apiRequest<ApiResponse<TaskSchedule>>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/schedule/`,
    { method: "DELETE" },
  );
  return res.data;
}

/* ---------- Task Deliverables ---------- */

export interface TaskDeliverable {
  id: string;
  task: string;
  frame: string;
  frame_title: string;
  frame_type: string;
  label: string;
  created_by_agent: string | null;
  created_at: string;
}

export async function fetchDeliverables(
  projectId: string,
  taskId: string,
): Promise<TaskDeliverable[]> {
  const res = await apiRequest<ApiResponse<TaskDeliverable[]>>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/deliverables/`,
  );
  return res.data;
}

/* ---------- Review queue (/review command) ---------- */

export interface ReviewQueueTask {
  task_id: string;
  ticket: string;
  title: string;
  status: TaskStatus;
  agent_id: string | null;
  frame_ids: string[];
  unread_notification_ids: string[];
}

export interface ReviewQueue {
  unread: ReviewQueueTask[];
  recent: ReviewQueueTask[];
}

export async function getReviewQueue(projectId: string): Promise<ReviewQueue> {
  const res = await apiRequest<ApiResponse<ReviewQueue>>(
    `/api/v1/projects/${projectId}/tasks/review-queue/`,
  );
  return res.data;
}

/* ---------- Task Comments ---------- */

export interface TaskComment {
  id: string;
  task: string;
  user: string | null;
  agent_id: string | null;
  agent_name?: string | null;
  mentioned_agent_id: string | null;
  content: string;
  created_at: string;
  images?: Array<{
    id: string;
    url: string;
    original_filename?: string;
    content_type?: string;
  }>;
  frame?: string | null;
  frame_title?: string | null;
  frame_type?: string | null;
  /**
   * Frame the agent produced while replying to this comment (e.g. created
   * or updated a note or image). Rendered as an inline FrameChip
   * under the comment bubble so the deliverable is one click away.
   */
  deliverable_frame?: string | null;
}

export async function fetchComments(
  projectId: string,
  taskId: string,
): Promise<TaskComment[]> {
  const res = await apiRequest<{ status: string; data: TaskComment[] }>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/comments/`,
  );
  return res.data;
}

export async function postComment(
  projectId: string,
  taskId: string,
  content: string,
  mentionedAgentId?: string | null,
  imageIds?: string[],
  frameId?: string | null,
): Promise<TaskComment> {
  const body: Record<string, unknown> = { content };
  if (mentionedAgentId) body.mentioned_agent_id = mentionedAgentId;
  if (imageIds && imageIds.length > 0) body.image_ids = imageIds;
  if (frameId) body.frame_id = frameId;
  const res = await apiRequest<{ status: string; data: TaskComment }>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/comments/`,
    { method: "POST", body },
  );
  return res.data;
}
